import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { text } from '../text';
import { hooks } from '../hooks';
import { components } from '../components';
import { theme } from '../constants';
import { useLanguage } from '../context/LanguageContext';
import axiosInstance from '../services/axiosInstance';
import { compressImage } from '../utils/imageUtils';
import { MdDelete, MdAdd, MdCameraAlt, MdClose, MdKeyboardArrowUp, MdKeyboardArrowDown, MdTableChart, MdViewAgenda } from 'react-icons/md';

const DeleteIcon = MdDelete as any;
const AddIcon = MdAdd as any;
const CameraIcon = MdCameraAlt as any;
const CloseIcon = MdClose as any;
const ArrowUpIcon = MdKeyboardArrowUp as any;
const ArrowDownIcon = MdKeyboardArrowDown as any;
const TableIcon = MdTableChart as any;
const CardIcon = MdViewAgenda as any;

const IMAGE_BASE = (process.env.REACT_APP_IMAGE_BASE_URL || '').replace(/\/$/, '');

// Pilihan tampilan rincian biaya (kartu / tabel ala Excel) diingat per perangkat
type LayoutMode = 'card' | 'table';
const LAYOUT_KEY = 'travelExpenseRecap.layout';
const readLayout = (): LayoutMode => {
  try { return localStorage.getItem(LAYOUT_KEY) === 'table' ? 'table' : 'card'; } catch { return 'card'; }
};

const parseAmt = (s: string) => s.replace(/\D/g, '');
const formatAmt = (s: string) => parseAmt(s).replace(/\B(?=(\d{3})+(?!\d))/g, '.');

interface RecapItem {
  description: string;
  expense_date: string;
  amount: string;
  employee_name: string;
  // Penanda kapal (task di grup perjalanan) — '' = biaya bersama
  task_id: string;
  photo: File | null;
  photoPreview: string | null;
  existingPhotoUrl: string | null;
}

const emptyItem = (): RecapItem => ({
  description: '',
  expense_date: '',
  amount: '',
  employee_name: '',
  task_id: '',
  photo: null,
  photoPreview: null,
  existingPhotoUrl: null,
});

const itemFromExisting = (item: any): RecapItem => ({
  description: item.description || '',
  expense_date: item.expense_date ? String(item.expense_date).substring(0, 10) : '',
  // parseFloat strips trailing .00 from DB numeric type
  amount: item.amount ? String(Math.round(parseFloat(String(item.amount)))) : '',
  employee_name: item.employee_name || '',
  task_id: item.task_id ? String(item.task_id) : '',
  photo: null,
  photoPreview: item.photo_url ? `${IMAGE_BASE}${item.photo_url}` : null,
  existingPhotoUrl: item.photo_url || null,
});

export const TravelExpenseRecapForm: React.FC = () => {
  const navigate = hooks.useAppNavigate();
  const { state } = useLocation();
  const { t } = useLanguage();
  const tr = (t as any).travelExpenseRecap;
  const task = state?.task;
  const existingRecap = state?.recap;
  const isEdit = !!existingRecap;

  const [fromLocation, setFromLocation] = useState(existingRecap?.from_location || '');
  const [toLocation, setToLocation] = useState(existingRecap?.to_location || '');
  const [departureDate, setDepartureDate] = useState(
    existingRecap?.departure_date ? String(existingRecap.departure_date).substring(0, 10) : ''
  );
  const [returnDate, setReturnDate] = useState(
    existingRecap?.return_date ? String(existingRecap.return_date).substring(0, 10) : ''
  );
  const [items, setItems] = useState<RecapItem[]>(
    existingRecap?.items?.length ? existingRecap.items.map(itemFromExisting) : [emptyItem()]
  );
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [layout, setLayout] = useState<LayoutMode>(readLayout);
  const tg = (t as any).tripGroup || {};
  // Grup perjalanan task asal rekapan — utk pilihan "dokumen bersama" & penanda kapal per item
  const ownerTaskId = existingRecap?.task_id || task?.id;
  const [groupTasks, setGroupTasks] = useState<{ id: number; vessel_name: string | null }[]>([]);
  const [isShared, setIsShared] = useState<boolean>(!!existingRecap?.is_shared);
  const inGroup = groupTasks.length > 1;
  useEffect(() => {
    if (!ownerTaskId) return;
    axiosInstance.get(`/travel-expense-recaps/group-tasks/${ownerTaskId}`).then(r => setGroupTasks(r.data || [])).catch(() => {});
  }, [ownerTaskId]);
  const showVessel = inGroup && isShared;
  const vesselOptions = [
    { value: '', label: tg.vesselShared || 'Bersama' },
    ...groupTasks.map((gt) => ({ value: String(gt.id), label: gt.vessel_name || `Task #${gt.id}` })),
  ];
  const fileInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const toggleLayout = () => {
    const next: LayoutMode = layout === 'card' ? 'table' : 'card';
    setLayout(next);
    try { localStorage.setItem(LAYOUT_KEY, next); } catch { /* storage tidak tersedia — cukup di state */ }
  };

  const updateItem = (idx: number, field: keyof RecapItem, val: string) => {
    setItems(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: val };
      return next;
    });
  };

  const handlePhotoChange = async (idx: number, file: File) => {
    try {
      const compressed = file.type.startsWith('image/') ? await compressImage(file) : file;
      const preview = URL.createObjectURL(compressed);
      setItems(prev => {
        const next = [...prev];
        if (next[idx].photoPreview && !next[idx].existingPhotoUrl) URL.revokeObjectURL(next[idx].photoPreview!);
        next[idx] = { ...next[idx], photo: compressed, photoPreview: preview };
        return next;
      });
    } catch {
      const preview = URL.createObjectURL(file);
      setItems(prev => {
        const next = [...prev];
        next[idx] = { ...next[idx], photo: file, photoPreview: preview };
        return next;
      });
    }
  };

  const removePhoto = (idx: number) => {
    setItems(prev => {
      const next = [...prev];
      const item = next[idx];
      if (item.photoPreview && !item.existingPhotoUrl) URL.revokeObjectURL(item.photoPreview!);
      next[idx] = { ...item, photo: null, photoPreview: null, existingPhotoUrl: null };
      return next;
    });
    if (fileInputRefs.current[idx]) fileInputRefs.current[idx]!.value = '';
  };

  const addRow = () => setItems(prev => [...prev, emptyItem()]);

  const removeRow = (idx: number) => {
    setItems(prev => {
      const item = prev[idx];
      if (item.photoPreview && !item.existingPhotoUrl) URL.revokeObjectURL(item.photoPreview!);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const moveItem = (idx: number, dir: -1 | 1) => {
    setItems(prev => {
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
      return next;
    });
  };

  const total = items.reduce((s, i) => s + (parseInt(parseAmt(i.amount) || '0', 10) || 0), 0);

  const doSubmit = async (submitStatus: 'draft' | 'submitted') => {
    setErrorMsg('');
    if (!fromLocation || !toLocation || !departureDate || !returnDate) {
      setErrorMsg(tr?.errorHeader || 'Semua field informasi perjalanan wajib diisi');
      return;
    }
    const validItems = items.filter(i => i.description.trim() && i.expense_date && i.amount);
    if (validItems.length === 0) {
      setErrorMsg(tr?.errorItems || 'Tambahkan minimal 1 rincian biaya yang lengkap');
      return;
    }
    if (!task?.id && !isEdit) {
      setErrorMsg(tr?.errorTask || 'Task tidak ditemukan');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('from_location', fromLocation);
      formData.append('to_location', toLocation);
      formData.append('departure_date', departureDate);
      formData.append('return_date', returnDate);
      // Selain draft = submit; tahap tujuan (verify/check/approval) dihitung backend
      formData.append('status', submitStatus);
      formData.append('is_shared', String(inGroup ? isShared : !!existingRecap?.is_shared));

      const payload: any[] = [];
      const photoIndexMap: number[] = [];

      items.forEach((item, idx) => {
        if (item.description.trim() && item.expense_date && item.amount) {
          photoIndexMap.push(idx);
          payload.push({
            description: item.description,
            expense_date: item.expense_date,
            amount: parseAmt(item.amount),
            employee_name: item.employee_name,
            task_id: showVessel && item.task_id ? Number(item.task_id) : null,
            existing_photo_url: item.photo ? null : item.existingPhotoUrl,
          });
        }
      });

      formData.append('items', JSON.stringify(payload));

      photoIndexMap.forEach((originalIdx, payloadIdx) => {
        const photo = items[originalIdx].photo;
        if (photo) formData.append(`photo_${payloadIdx}`, photo);
      });

      const config = { headers: { 'Content-Type': 'multipart/form-data' } };
      if (isEdit) {
        await axiosInstance.put(`/travel-expense-recaps/${existingRecap.id}`, formData, config);
      } else {
        await axiosInstance.post(`/travel-expense-recaps/task/${task.id}`, formData, config);
      }
      navigate(-1);
    } catch (err: any) {
      setErrorMsg(err.response?.data?.message || tr?.errorSave || 'Gagal menyimpan rekapan');
    } finally {
      setSubmitting(false);
    }
  };

  // minWidth 0: input type=date punya lebar minimum bawaan browser yg bisa mendorong keluar container flex
  const inputStyle: React.CSSProperties = {
    width: '100%',
    minWidth: 0,
    border: '1px solid #D1D5DB',
    borderRadius: 10,
    padding: '10px 14px',
    fontSize: 14,
    boxSizing: 'border-box',
    outline: 'none',
    fontFamily: 'inherit',
    color: theme.colors.mainDark,
    backgroundColor: '#fff',
  };

  const labelStyle: React.CSSProperties = { color: '#6B7280', marginBottom: 5, display: 'block', fontSize: 12 };

  // Gaya sel tabel ala Excel: garis grid tipis, input tanpa border mengisi penuh sel
  const thStyle: React.CSSProperties = {
    backgroundColor: '#F3F4F6', color: '#374151', fontSize: 12, fontWeight: 600, textAlign: 'left',
    padding: '8px 8px', border: '1px solid #E5E7EB', whiteSpace: 'nowrap',
  };
  const tdStyle: React.CSSProperties = { border: '1px solid #E5E7EB', padding: 0, backgroundColor: '#fff', verticalAlign: 'middle' };
  const cellInputStyle: React.CSSProperties = {
    width: '100%', border: 'none', padding: '9px 8px', fontSize: 13, boxSizing: 'border-box', outline: 'none',
    fontFamily: 'inherit', color: theme.colors.mainDark, backgroundColor: 'transparent',
  };

  const renderPhotoInput = (idx: number) => (
    <input
      ref={el => { fileInputRefs.current[idx] = el; }}
      type="file"
      accept="image/*"
      style={{ display: 'none' }}
      onChange={e => { const f = e.target.files?.[0]; if (f) handlePhotoChange(idx, f); }}
    />
  );

  const renderTable = () => (
    <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid #E5E7EB', marginBottom: 8 }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 720 }}>
        <thead>
          <tr>
            <th style={{ ...thStyle, width: 36, textAlign: 'center' }}>#</th>
            <th style={{ ...thStyle, width: 140 }}>{tr?.date || 'Tanggal'}</th>
            <th style={{ ...thStyle, minWidth: 200 }}>{tr?.description || 'Deskripsi Biaya'}</th>
            <th style={{ ...thStyle, width: 130, textAlign: 'right' }}>{tr?.amount || 'Nominal (Rp)'}</th>
            <th style={{ ...thStyle, minWidth: 150 }}>{tr?.colNotes || 'Notes'}</th>
            {showVessel && <th style={{ ...thStyle, minWidth: 130 }}>{tg.vessel || 'Kapal'}</th>}
            <th style={{ ...thStyle, width: 64, textAlign: 'center' }}>{tr?.colReceipt || 'Bukti'}</th>
            <th style={{ ...thStyle, width: 36 }} />
          </tr>
        </thead>
        <tbody>
          {items.map((item, idx) => (
            <tr key={idx}>
              <td style={{ ...tdStyle, backgroundColor: '#F9FAFB', textAlign: 'center', fontSize: 12, color: '#6B7280' }}>{idx + 1}</td>
              <td style={tdStyle}>
                <input type="date" value={item.expense_date}
                  onChange={e => updateItem(idx, 'expense_date', e.target.value)} style={cellInputStyle} />
              </td>
              <td style={tdStyle}>
                <input value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)}
                  placeholder={tr?.descriptionPlaceholder || 'Contoh: Maxim dari bandara ke kapal'} style={cellInputStyle} />
              </td>
              <td style={tdStyle}>
                <input type="text" inputMode="numeric" value={formatAmt(item.amount)}
                  onChange={e => updateItem(idx, 'amount', parseAmt(e.target.value))}
                  placeholder="0" style={{ ...cellInputStyle, textAlign: 'right' }} />
              </td>
              <td style={tdStyle}>
                <input value={item.employee_name} onChange={e => updateItem(idx, 'employee_name', e.target.value)}
                  placeholder={tr?.notesPlaceholder || 'Mis: nama teknisi, keterangan tambahan'} style={cellInputStyle} />
              </td>
              {showVessel && (
                <td style={tdStyle}>
                  <select value={item.task_id} onChange={e => updateItem(idx, 'task_id', e.target.value)} style={cellInputStyle}>
                    {vesselOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </td>
              )}
              <td style={{ ...tdStyle, textAlign: 'center', padding: 4 }}>
                {item.photoPreview ? (
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <img src={item.photoPreview} alt="bukti" onClick={() => setLightboxUrl(item.photoPreview)}
                      style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 6, cursor: 'pointer', display: 'block' }} />
                    <div onClick={() => removePhoto(idx)}
                      style={{
                        position: 'absolute', top: -5, right: -5, width: 16, height: 16, borderRadius: 8,
                        backgroundColor: '#EF4444', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                      }}>
                      <CloseIcon size={10} color="#fff" />
                    </div>
                  </div>
                ) : (
                  <div onClick={() => fileInputRefs.current[idx]?.click()} style={{ cursor: 'pointer', padding: 6, display: 'inline-flex' }}>
                    <CameraIcon size={20} color="#9CA3AF" />
                  </div>
                )}
                {renderPhotoInput(idx)}
              </td>
              <td style={{ ...tdStyle, textAlign: 'center' }}>
                {items.length > 1 && (
                  <div onClick={() => removeRow(idx)} style={{ cursor: 'pointer', padding: 6, display: 'inline-flex' }}>
                    <DeleteIcon size={16} color="#EF4444" />
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={3} style={{ ...tdStyle, ...thStyle, textAlign: 'right' }}>{tr?.total || 'Total'}</td>
            <td style={{ ...tdStyle, ...thStyle, textAlign: 'right', color: '#059669' }}>{total.toLocaleString('id-ID')}</td>
            <td colSpan={showVessel ? 4 : 3} style={{ ...tdStyle, ...thStyle }} />
          </tr>
        </tfoot>
      </table>
    </div>
  );

  const layoutToggle = (
    <div
      onClick={toggleLayout}
      title={layout === 'card' ? (tr?.layoutTable || 'Tampilan tabel') : (tr?.layoutCard || 'Tampilan kartu')}
      style={{ cursor: 'pointer', padding: '0 16px', height: '100%', display: 'flex', alignItems: 'center' }}
    >
      {layout === 'card'
        ? <TableIcon size={22} color={theme.colors.mainDark} />
        : <CardIcon size={22} color={theme.colors.mainDark} />}
    </div>
  );

  return (
    <div id="screen" style={{ backgroundColor: '#F9FAFB', minHeight: '100vh' }}>
      <components.Header
        title={isEdit ? (tr?.editTitle || 'Edit Rekapan Biaya') : (tr?.addTitle || 'Rekapan Biaya Perjalanan')}
        goBack={true}
        rightContent={layoutToggle}
      />

      <main className="container" style={{ marginTop: 60, paddingBottom: 130, height: 'calc(100vh - 60px)', overflowY: 'auto' }}>
        <div style={{ padding: '10px 8px', display: 'flex', flexDirection: 'column', gap: 12 }}>

          {errorMsg && (
            <div style={{ backgroundColor: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 10, padding: '10px 14px' }}>
              <text.T14 style={{ color: '#DC2626' }}>{errorMsg}</text.T14>
            </div>
          )}

          {inGroup && (
            <components.SharedDocToggle checked={isShared} onChange={setIsShared} label={tg.shareToggle} hint={tg.shareHint} />
          )}

          {isEdit && existingRecap?.status === 'draft' && (
            <div style={{ backgroundColor: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 10, padding: '10px 14px' }}>
              <text.T12 style={{ color: '#92400E' }}>{tr?.draftNotice || 'Draft — belum dikirim untuk review.'}</text.T12>
            </div>
          )}

          {/* Route + Dates */}
          <div style={{ backgroundColor: '#fff', borderRadius: 14, padding: 14, border: '1px solid #E5E7EB' }}>
            <text.T14 style={{ fontWeight: '600', color: '#111827', marginBottom: 12, display: 'block' }}>
              {tr?.tripInfo || 'Informasi Perjalanan'}
            </text.T14>
            <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <label style={labelStyle}>{tr?.from || 'Berangkat Dari'}</label>
                <input
                  value={fromLocation}
                  onChange={e => setFromLocation(e.target.value)}
                  placeholder={tr?.fromPlaceholder || 'Kota asal'}
                  style={inputStyle}
                />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <label style={labelStyle}>{tr?.to || 'Tujuan Ke'}</label>
                <input
                  value={toLocation}
                  onChange={e => setToLocation(e.target.value)}
                  placeholder={tr?.toPlaceholder || 'Kota tujuan'}
                  style={inputStyle}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <label style={labelStyle}>{tr?.departure || 'Tanggal Berangkat'}</label>
                <input type="date" value={departureDate} onChange={e => setDepartureDate(e.target.value)} style={inputStyle} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <label style={labelStyle}>{tr?.returnDate || 'Tanggal Kembali'}</label>
                <input type="date" value={returnDate} onChange={e => setReturnDate(e.target.value)} style={inputStyle} />
              </div>
            </div>
          </div>

          {/* Expense rows */}
          <div>
            <text.T14 style={{ fontWeight: '600', color: '#374151', marginBottom: 8, display: 'block' }}>
              {tr?.costItems || 'Rincian Biaya'}
            </text.T14>

            {layout === 'table' ? renderTable() : items.map((item, idx) => (
              <div key={idx} style={{ backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: 14, marginBottom: 8, overflow: 'hidden' }}>
                <div style={{ backgroundColor: '#F0FDF4', padding: '8px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <text.T12 style={{ fontWeight: '700', color: '#059669' }}>#{idx + 1}</text.T12>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <div
                      onClick={() => idx > 0 && moveItem(idx, -1)}
                      style={{ cursor: idx > 0 ? 'pointer' : 'default', padding: 4, opacity: idx > 0 ? 1 : 0.3 }}
                    >
                      <ArrowUpIcon size={18} color="#059669" />
                    </div>
                    <div
                      onClick={() => idx < items.length - 1 && moveItem(idx, 1)}
                      style={{ cursor: idx < items.length - 1 ? 'pointer' : 'default', padding: 4, opacity: idx < items.length - 1 ? 1 : 0.3 }}
                    >
                      <ArrowDownIcon size={18} color="#059669" />
                    </div>
                    {items.length > 1 && (
                      <div onClick={() => removeRow(idx)} style={{ cursor: 'pointer', padding: 4 }}>
                        <DeleteIcon size={16} color="#EF4444" />
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div>
                    <label style={labelStyle}>{tr?.description || 'Deskripsi Biaya'}</label>
                    <input
                      value={item.description}
                      onChange={e => updateItem(idx, 'description', e.target.value)}
                      placeholder={tr?.descriptionPlaceholder || 'Contoh: Maxim dari bandara ke kapal'}
                      style={inputStyle}
                    />
                  </div>

                  <div style={{ display: 'flex', gap: 10 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <label style={labelStyle}>{tr?.date || 'Tanggal'}</label>
                      <input
                        type="date"
                        value={item.expense_date}
                        onChange={e => updateItem(idx, 'expense_date', e.target.value)}
                        style={inputStyle}
                      />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <label style={labelStyle}>{tr?.amount || 'Nominal (Rp)'}</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={formatAmt(item.amount)}
                        onChange={e => updateItem(idx, 'amount', parseAmt(e.target.value))}
                        placeholder="0"
                        style={inputStyle}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={labelStyle}>{tr?.notes || 'Notes (opsional)'}</label>
                    <input
                      value={item.employee_name}
                      onChange={e => updateItem(idx, 'employee_name', e.target.value)}
                      placeholder={tr?.notesPlaceholder || 'Mis: nama teknisi, keterangan tambahan'}
                      style={inputStyle}
                    />
                  </div>

                  {showVessel && (
                    <div>
                      <label style={labelStyle}>{tg.vessel || 'Kapal'}</label>
                      <select value={item.task_id} onChange={e => updateItem(idx, 'task_id', e.target.value)} style={inputStyle}>
                        {vesselOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                  )}

                  <div>
                    <label style={labelStyle}>{tr?.photoLabel || 'Foto Bukti / struk (opsional)'}</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      {item.photoPreview ? (
                        <div style={{ position: 'relative', display: 'inline-block' }}>
                          <img
                            src={item.photoPreview}
                            alt="bukti"
                            onClick={() => setLightboxUrl(item.photoPreview)}
                            style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 10, cursor: 'pointer', border: '1px solid #E5E7EB' }}
                          />
                          <div
                            onClick={() => removePhoto(idx)}
                            style={{
                              position: 'absolute', top: -6, right: -6, width: 20, height: 20,
                              borderRadius: 10, backgroundColor: '#EF4444',
                              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                            }}
                          >
                            <CloseIcon size={12} color="#fff" />
                          </div>
                        </div>
                      ) : (
                        <div
                          onClick={() => fileInputRefs.current[idx]?.click()}
                          style={{
                            width: 72, height: 72, borderRadius: 10,
                            border: '1.5px dashed #D1D5DB', backgroundColor: '#F9FAFB',
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                            gap: 4, cursor: 'pointer',
                          }}
                        >
                          <CameraIcon size={22} color="#9CA3AF" />
                          <text.T10 style={{ color: '#9CA3AF' }}>Upload</text.T10>
                        </div>
                      )}
                      <text.T12 style={{ color: '#9CA3AF', flex: 1 }}>
                        {item.photoPreview
                          ? (tr?.photoTapHint || 'Tap foto untuk perbesar')
                          : (tr?.photoHint || 'Upload foto struk atau bukti pembayaran')}
                      </text.T12>
                      {renderPhotoInput(idx)}
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <div
              onClick={addRow}
              style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', border: '1.5px dashed #93C5FD', borderRadius: 12, backgroundColor: '#EFF6FF' }}
            >
              <AddIcon size={18} color="#3B82F6" />
              <text.T14 style={{ color: '#3B82F6' }}>{tr?.addRow || 'Tambah Baris'}</text.T14>
            </div>
          </div>

          {/* Total */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', backgroundColor: '#F0FDF4', borderRadius: 12, border: '1px solid #BBF7D0' }}>
            <text.T16 style={{ fontWeight: '700', color: '#065F46' }}>{tr?.totalLabel || 'Total Pengeluaran'}</text.T16>
            <text.T16 style={{ fontWeight: '700', color: '#059669' }}>Rp {total.toLocaleString('id-ID')}</text.T16>
          </div>

        </div>
      </main>

      {/* Bottom buttons */}
      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, maxWidth: 650, margin: '0 auto', padding: '10px 16px 14px', backgroundColor: '#fff', borderTop: '1px solid #F3F4F6', display: 'flex', gap: 10 }}>
        <components.Button
          title={submitting ? '...' : (tr?.saveDraft || 'Simpan Draft')}
          onClick={submitting ? undefined : () => doSubmit('draft')}
          colorScheme="light"
          containerStyle={{ flex: 1, opacity: submitting ? 0.6 : 1 }}
        />
        <components.Button
          title={submitting ? (t.common.submitting || 'Mengirim...') : (tr?.submit || 'Kirim')}
          onClick={submitting ? undefined : () => doSubmit('submitted')}
          containerStyle={{ flex: 2, opacity: submitting ? 0.6 : 1 }}
        />
      </div>

      {/* Lightbox */}
      {lightboxUrl && (
        <div
          onClick={() => setLightboxUrl(null)}
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        >
          <img src={lightboxUrl} alt="preview" style={{ maxWidth: '100%', maxHeight: '90vh', borderRadius: 10, objectFit: 'contain' }} />
        </div>
      )}
    </div>
  );
};
